// @ts-check
/**
 * ProgressStore — ukladanie pokroku a nastavení.
 *
 * Dnes je to `localStorage`, ale celý zvyšok aplikácie pozná iba toto rozhranie.
 * Keď neskôr pribudnú účty, stačí pridať `HttpProgressStore` a vymeniť implementáciu —
 * obrazovky sa nemenia. Preto je dôležité, že tu nie je ani jedna referencia na DOM.
 *
 * Schéma je verzovaná (`VERSION`), aby sa pri budúcich zmenách dalo migrovať
 * a používateľom nezmizol pokrok.
 */

export const STORAGE_VERSION = 1;
export const STORAGE_KEY = 'atf-typing:progress';

/**
 * @typedef {Object} Settings
 * @property {string} layoutId
 * @property {'correct'|'flow'} errorMode
 * @property {boolean} allowBackspace
 * @property {boolean} tolerateDiacritics
 * @property {number} netPenalty
 * @property {boolean} officialStrokes   počítať údery podľa oficiálnej metodiky
 * @property {number} targetSpeed        cieľová rýchlosť v čistých úderoch/min (50–300)
 * @property {number} targetErrorRate    cieľová chybovosť v % (0,1–1,5)
 * @property {boolean} showKeyboard
 * @property {'sm'|'md'|'lg'} fontSize
 */

/** @type {Settings} */
export const DEFAULT_SETTINGS = {
  layoutId: 'sk-qwertz',
  errorMode: 'correct',
  allowBackspace: true,
  tolerateDiacritics: false,
  netPenalty: 10,
  officialStrokes: true,
  targetSpeed: 140,
  targetErrorRate: 0.3,
  showKeyboard: true,
  fontSize: 'md',
};

/**
 * @typedef {Object} Attempt
 * @property {string|null} lessonId
 * @property {string|null} lessonTitle
 * @property {number} finishedAt
 * @property {number} durationMs
 * @property {number} grossStrokes
 * @property {number} correct
 * @property {number} errors
 * @property {number} wrongChars
 * @property {number} netCpm
 * @property {number} grossCpm
 * @property {number} wpm
 * @property {number} accuracy
 * @property {Array<{char: string, errors: number}>} perKeyErrors
 * @property {boolean} completed
 * @property {number} [stars]
 * @property {number} [grade]   známka 1–5 podľa metodiky predlohy (horšia z rýchlosti a chybovosti)
 * @property {string|null} [mode]  režim cvičenia (hmaty, slova, vety, text)
 * @property {number} [errorRatePercent]
 * @property {number} [keystrokes]
 */

/** Pamäťová náhrada pre prostredia bez localStorage (testy, SSR). */
function memoryStorage() {
  /** @type {Map<string, string>} */
  const map = new Map();
  return {
    getItem: (/** @type {string} */ k) => map.get(k) ?? null,
    setItem: (/** @type {string} */ k, /** @type {string} */ v) => void map.set(k, v),
    removeItem: (/** @type {string} */ k) => void map.delete(k),
  };
}

function safeStorage() {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('__atf_probe', '1');
      localStorage.removeItem('__atf_probe');
      return localStorage;
    }
  } catch {
    // napr. súkromný režim alebo zakázané cookies
  }
  return memoryStorage();
}

export class ProgressStore {
  /** @param {{getItem: Function, setItem: Function, removeItem: Function}} [storage] */
  constructor(storage = safeStorage()) {
    this.storage = storage;
    /** @type {Set<() => void>} */
    this.listeners = new Set();
    /**
     * Číslo, ktoré sa zmení pri každej zmene dát. React (`useSyncExternalStore`)
     * potrebuje hodnotu, ktorá mení identitu — samotné `data` sa menia na mieste.
     */
    this.revision = 0;
    this.data = this.#read();
  }

  /**
   * @returns {{version: number, attempts: Attempt[], settings: Settings, calibration: Record<string, Record<string, string>>}}
   */
  #read() {
    const empty = {
      version: STORAGE_VERSION,
      attempts: /** @type {Attempt[]} */ ([]),
      settings: { ...DEFAULT_SETTINGS },
      calibration: /** @type {Record<string, Record<string, string>>} */ ({}),
    };
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) return empty;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return empty;
      return {
        version: STORAGE_VERSION,
        attempts: Array.isArray(parsed.attempts) ? parsed.attempts : [],
        settings: { ...DEFAULT_SETTINGS, ...(parsed.settings ?? {}) },
        calibration: parsed.calibration ?? {},
      };
    } catch {
      return empty;
    }
  }

  #write() {
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch {
      // plný disk / zakázané úložisko — appka musí fungovať ďalej, len bez histórie
    }
    this.emit();
  }

  /** @param {() => void} listener */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit() {
    this.revision += 1;
    for (const listener of this.listeners) listener();
  }

  /** @returns {Settings} */
  getSettings() {
    return this.data.settings;
  }

  /** @param {Partial<Settings>} patch */
  updateSettings(patch) {
    this.data.settings = { ...this.data.settings, ...patch };
    this.#write();
  }

  /** @param {Attempt} attempt */
  recordAttempt(attempt) {
    this.data.attempts.push(attempt);
    // História nesmie rásť donekonečna (localStorage má ~5 MB).
    if (this.data.attempts.length > 500) {
      this.data.attempts = this.data.attempts.slice(-500);
    }
    this.#write();
  }

  /**
   * @param {{lessonId?: string|null, limit?: number, mode?: string|null}} [filter]
   * @returns {Attempt[]}
   */
  attempts(filter = {}) {
    const { lessonId, limit, mode } = filter;
    let list = this.data.attempts;
    if (lessonId) list = list.filter((a) => a.lessonId === lessonId || a.lessonId?.startsWith(`${lessonId}:`));
    if (mode) list = list.filter((a) => a.mode === mode);
    if (limit) list = list.slice(-limit);
    return list;
  }

  /**
   * Najlepší pokus lekcie podľa čistého tempa.
   * @param {string} lessonId
   * @param {string} [mode] obmedziť na jeden režim cvičenia
   */
  best(lessonId, mode) {
    const list = this.attempts({ lessonId, mode });
    if (list.length === 0) return null;
    return list.reduce((best, a) => (a.netCpm > best.netCpm ? a : best), list[0]);
  }

  /**
   * Najlepšie hviezdičky lekcie.
   * @param {string} lessonId
   * @param {string} [mode]
   */
  stars(lessonId, mode) {
    const best = this.best(lessonId, mode);
    return best?.stars ?? 0;
  }

  completedLessonIds() {
    return [
      ...new Set(
        this.attempts()
          .filter((a) => a.completed && a.lessonId)
          .map((a) => /** @type {string} */ (a.lessonId).split(':')[0]),
      ),
    ];
  }

  /** Najčastejšie chybované znaky naprieč históriou. */
  weakKeys(limit = 8) {
    /** @type {Map<string, number>} */
    const totals = new Map();
    for (const attempt of this.data.attempts) {
      for (const { char, errors } of attempt.perKeyErrors ?? []) {
        totals.set(char, (totals.get(char) ?? 0) + errors);
      }
    }
    return [...totals.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([char, errors]) => ({ char, errors }));
  }

  /**
   * Kalibrácia rozloženia: code → skutočný znak, ktorý klávesa posiela.
   * @param {string} layoutId
   */
  calibration(layoutId) {
    return this.data.calibration[layoutId] ?? {};
  }

  /** @param {string} layoutId @param {Record<string, string>} map */
  setCalibration(layoutId, map) {
    this.data.calibration[layoutId] = { ...this.calibration(layoutId), ...map };
    this.#write();
  }

  /** @param {string} layoutId */
  clearCalibration(layoutId) {
    delete this.data.calibration[layoutId];
    this.#write();
  }

  /** Záloha pokroku (napr. prenos na iný počítač, keď nie sú účty). */
  export() {
    return JSON.stringify(this.data, null, 2);
  }

  /** @param {string} json */
  import(json) {
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.attempts)) {
      throw new Error('Neplatný súbor so zálohou pokroku.');
    }
    this.data = {
      version: STORAGE_VERSION,
      attempts: parsed.attempts,
      settings: { ...DEFAULT_SETTINGS, ...(parsed.settings ?? {}) },
      calibration: parsed.calibration ?? {},
    };
    this.#write();
  }

  reset() {
    this.data = {
      version: STORAGE_VERSION,
      attempts: [],
      settings: { ...DEFAULT_SETTINGS },
      calibration: {},
    };
    this.#write();
  }
}
